import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChevronDown, ChevronLeft } from 'lucide-react-native';
import { ReactNode, memo } from 'react';
import { Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/** Widest the dashboard pages' content grows. */
export const kBiContentMaxWidth = 1440;

/** The fixed white header of the BI pages: back, title, actions, extras. */
export const BiPageHeader = memo(
  ({
    title,
    badge,
    actions,
    onBack,
    onPressTitle,
    children,
  }: {
    title: string;
    /** Makes the title a picker (title + ▾), e.g. to switch dashboards. */
    onPressTitle?: () => void;
    badge?: ReactNode;
    actions?: ReactNode;
    onBack: () => void;
    /** Rows under the title, e.g. the date range bar. */
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    return (
      <View
        style={{
          paddingTop: insets.top + rpx(10),
          paddingBottom: rpx(12),
          paddingHorizontal: rpx(16),
          backgroundColor: aiColors.card,
          borderBottomWidth: 1,
          borderBottomColor: aiColors.border,
        }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: kBiContentMaxWidth,
            alignSelf: 'center',
            gap: rpx(12),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: rpx(8),
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="返回"
              onPress={onBack}
              hitSlop={8}
              style={{
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChevronLeft size={20} color={aiColors.foreground} />
            </Pressable>
            <View
              style={{
                flex: 1,
                minWidth: rpx(120),
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(8),
              }}
            >
              <Pressable
                accessibilityRole={onPressTitle ? 'button' : undefined}
                accessibilityLabel={onPressTitle ? `${title}，切换看板` : title}
                accessibilityHint={onPressTitle ? '打开看板列表' : undefined}
                disabled={!onPressTitle}
                onPress={onPressTitle}
                hitSlop={6}
                style={({ pressed }) => ({
                  flexShrink: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: rpx(4),
                  opacity: pressed ? 0.6 : 1,
                })}
              >
                <Typography.Text
                  bold
                  size={rpx(16)}
                  color={aiColors.foreground}
                  numberOfLines={1}
                  style={{ flexShrink: 1 }}
                >
                  {title}
                </Typography.Text>
                {onPressTitle ? (
                  <ChevronDown size={16} color={aiColors.mutedForeground} />
                ) : null}
              </Pressable>
              {badge}
            </View>
            {actions ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: rpx(8),
                }}
              >
                {actions}
              </View>
            ) : null}
          </View>
          {children}
        </View>
      </View>
    );
  },
);
